/**
 * modules/loadingScreen.js
 * ローディング画面の制御
 *
 * 役割:
 * - 表示条件確認（is-loading-screen-pending チェック）
 * - pending → running クラス切り替え（CSS アニメーション開始）
 * - 終了クラス付与 → DOM 除去
 * - フェイルセーフタイマー管理
 * - pageshow (bfcache) 対応
 *
 * CSS アニメーションの大部分は _loading-screen.scss が担当。
 */

import { prefersReducedMotion } from "../core/prefers-reduced-motion.js";

const SS_KEY = "souwa_loading_screen_seen_v1";

export function initLoadingScreen() {
  const screen = document.getElementById("loading-screen");

  // loading-screen 要素がなければトップページ以外 → 何もしない
  if (!screen) return;

  const html = document.documentElement;

  // pending クラスがなければ「このセッション・このナビゲーションでは非表示」
  if (!html.classList.contains("is-loading-screen-pending")) {
    // CSS の display:none でカバー済み。DOM は残したまま（特定の副作用なし）
    return;
  }

  // インラインスクリプトが設定したフェイルセーフタイマーを解除
  const failsafeId = html.dataset.souwaLoadingFailsafe;
  if (failsafeId) {
    clearTimeout(Number(failsafeId));
    delete html.dataset.souwaLoadingFailsafe;
  }

  const reduced = prefersReducedMotion();

  // pending → running（CSS アニメーション開始トリガー）
  html.classList.remove("is-loading-screen-pending");
  html.classList.add("is-loading-screen-running");

  // 終了タイミング
  // 通常: 3.35s（英語ロゴタイプ表示 2.45s + 余韻 0.90s）
  // reduced-motion: 0.8s（静止表示後に短いフェード）
  const exitDelay = reduced ? 800 : 3350;

  let exitTimer = setTimeout(() => {
    startExit(screen, html);
  }, exitDelay);

  // フェイルセーフ（5s 後に強制解除）
  const safeTimer = setTimeout(() => {
    clearTimeout(exitTimer);
    forceEnd(screen, html);
  }, 5000);

  // pageshow: bfcache から戻った場合はローディング画面を即除去
  window.addEventListener(
    "pageshow",
    (e) => {
      if (e.persisted) {
        clearTimeout(exitTimer);
        clearTimeout(safeTimer);
        forceEnd(screen, html);
      }
    },
    { once: true }
  );
}

/**
 * フェードアウト開始（running → exiting）
 */
function startExit(screen, html) {
  // running を外して exiting を同フレームで付与（display:none 瞬間を防止）
  html.classList.remove("is-loading-screen-running");
  html.classList.add("is-loading-screen-exiting");

  // CSS フェードアウトアニメーション完了後（0.32s）に DOM 除去
  setTimeout(() => forceEnd(screen, html), 380);
}

/**
 * 全クラスを除去して DOM から削除
 */
function forceEnd(screen, html) {
  html.classList.remove(
    "is-loading-screen-pending",
    "is-loading-screen-running",
    "is-loading-screen-exiting"
  );
  if (screen && screen.parentNode) {
    screen.parentNode.removeChild(screen);
  }
}
